/**
 * Cross-selling del carrello (BAU-34).
 *
 * Pannello affiancato al minicart su desktop, sheet dal basso su mobile.
 * Gestisce selezione variante e aggiunta rapida, senza ricaricare la pagina:
 * dopo l'aggiunta emette `cart:refresh`, che il tema usa gia' per rigenerare
 * body e footer del drawer via Section Rendering API.
 *
 * Il pannello vive fuori da #CartDrawer-<id> (e' fratello di .drawer__inner, per
 * poter uscire dal suo overflow: hidden), quindi il refresh del tema non lo tocca:
 * si ricarica da solo ascoltando cartUpdate.
 */
class CartCrossSell extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.refreshTimer = null;
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);

    if (window.FoxTheme && FoxTheme.pubsub) {
      this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.onCartUpdate
      );
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  }

  open() {
    this.setAttribute('open', '');
    this.syncToggles(true);
  }

  close() {
    this.removeAttribute('open');
    this.syncToggles(false);
  }

  /** I toggle vivono nel footer, fuori da questo elemento: vanno tenuti allineati a mano. */
  syncToggles(expanded) {
    document
      .querySelectorAll(`[data-cross-sell-toggle][aria-controls="${this.id}"]`)
      .forEach((toggle) => toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
  }

  onClick(event) {
    if (event.target.closest('[data-cross-sell-close]')) {
      this.close();
      return;
    }

    const addButton = event.target.closest('[data-cross-sell-add]');
    if (addButton) this.addToCart(addButton);
  }

  /** Il prezzo formattato e' gia' pronto nell'option: qui si sposta solo il testo. */
  onChange(event) {
    const select = event.target.closest('[data-cross-sell-variants]');
    if (!select || !select.options) return;

    const item = select.closest('[data-cross-sell-item]');
    const priceEl = item && item.querySelector('[data-cross-sell-price]');
    const price = select.options[select.selectedIndex]?.dataset.price;

    if (priceEl && price) priceEl.textContent = price;
  }

  async addToCart(button) {
    const item = button.closest('[data-cross-sell-item]');
    const input = item && item.querySelector('[data-cross-sell-variants]');
    const variantId = input && input.value;
    if (!variantId) return;

    button.setAttribute('disabled', '');
    button.classList.add('is-loading');

    try {
      const response = await fetch(`${FoxTheme.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message);

      this.announce(this.dataset.addedText);
      this.close();

      // Il tema ricostruisce body e footer del drawer da qui; nessun reload.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      this.announce(this.dataset.errorText);
      console.error('Cross-sell: aggiunta al carrello fallita', error);
      button.removeAttribute('disabled');
    } finally {
      button.classList.remove('is-loading');
    }
  }

  announce(message) {
    const status = this.querySelector('[data-cross-sell-status]');
    if (status && message) status.textContent = message;
  }

  /**
   * Ricarica il proprio contenuto dopo un aggiornamento del carrello: i prodotti
   * appena aggiunti vanno tolti dai suggerimenti e i correlati possono cambiare.
   * Debounce perche' un ritocco alle quantita' pubblica piu' eventi ravvicinati.
   */
  onCartUpdate() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => this.refresh(), 300);
  }

  async refresh() {
    try {
      const response = await fetch(`${FoxTheme.routes.root_url}?section_id=${this.sectionId}`);
      if (!response.ok) return;

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = parsed.getElementById(this.id);
      if (!fresh) return;

      this.innerHTML = fresh.innerHTML;
      this.toggleAttribute('hidden', fresh.hasAttribute('hidden'));
      if (fresh.hasAttribute('hidden')) this.close();
    } catch (error) {
      console.error('Cross-sell: refresh fallito', error);
    }
  }
}
customElements.define('cart-cross-sell', CartCrossSell);

/**
 * Il toggle sta nel footer del drawer, che il tema rigenera via innerHTML a ogni
 * aggiornamento: un listener diretto sul bottone verrebbe perso, quindi si delega
 * al documento.
 */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-cross-sell-toggle]');
  if (!toggle) return;

  const panel = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!panel) return;

  panel.hasAttribute('open') ? panel.close() : panel.open();
});
